/**
 * YUMIX — Injury Capture Dialog (Pipeline Injury-to-Workout).
 *
 * Quick capture flow that drives the new POST /api/injury-workout/generate
 * endpoint. Tier-aware response handling:
 *   • Medical-shield block → strict disclaimer (Calendly button only for ELITE)
 *   • Tier paywall (FREE) → upgrade nudge
 *   • Plan generated (PREMIUM/ELITE) → success toast + heroes + redirect to /workout
 *
 * Triggered from BOTH:
 *   1. Settings → Mode Blessure toggle ON
 *   2. Dedicated dashboard FAB "🩹 Activer mode blessure"
 *
 * (Yuri's directive: trigger from both places.)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Slider } from './ui/slider';
import { ShieldAlert, Activity, Sparkles, Loader2, CalendarDays, Lock } from 'lucide-react';
import { useLanguage, api } from '../context/AppContext';
import ErrorLogger from '../utils/errorLogger';

const ZONES = [
  { id: 'knee_right',     fr: 'Genou droit',       en: 'Right knee' },
  { id: 'knee_left',      fr: 'Genou gauche',      en: 'Left knee' },
  { id: 'shoulder_right', fr: 'Épaule droite',     en: 'Right shoulder' },
  { id: 'shoulder_left',  fr: 'Épaule gauche',     en: 'Left shoulder' },
  { id: 'back_lower',     fr: 'Bas du dos',        en: 'Lower back' },
  { id: 'back_upper',     fr: 'Haut du dos',       en: 'Upper back' },
  { id: 'ankle_right',    fr: 'Cheville droite',   en: 'Right ankle' },
  { id: 'ankle_left',     fr: 'Cheville gauche',   en: 'Left ankle' },
  { id: 'wrist_right',    fr: 'Poignet droit',     en: 'Right wrist' },
  { id: 'wrist_left',     fr: 'Poignet gauche',    en: 'Left wrist' },
  { id: 'hip_right',      fr: 'Hanche droite',     en: 'Right hip' },
  { id: 'hip_left',       fr: 'Hanche gauche',     en: 'Left hip' },
  { id: 'neck',           fr: 'Cou / cervical',    en: 'Neck / cervical' },
  { id: 'other',          fr: 'Autre',             en: 'Other' },
];

const OBJECTIVES = [
  { id: 'recover',  fr: 'Récupération totale', en: 'Full recovery' },
  { id: 'maintain', fr: 'Maintien',            en: 'Maintain' },
  { id: 'adapt',    fr: 'Adaptation continue', en: 'Continued adaptation' },
];

const InjuryCaptureDialog = ({ open, onOpenChange, onPlanGenerated }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fr = language.startsWith('fr');

  const [zone, setZone] = useState('');
  const [severity, setSeverity] = useState([4]);
  const [description, setDescription] = useState('');
  const [objective, setObjective] = useState('maintain');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // { blocked, ... }

  const reset = () => {
    setZone(''); setSeverity([4]); setDescription(''); setObjective('maintain');
    setSubmitting(false); setResult(null);
  };

  const handleSubmit = async () => {
    if (!zone) {
      toast.error(fr ? 'Choisis la zone affectée' : 'Pick the affected zone');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post('/injury-workout/generate', {
        zone,
        severity_score: severity[0],
        description,
        objective,
        language,
      });
      setResult(data);

      if (data.blocked) {
        // Medical shield OR tier paywall — don't close, show explanation.
        return;
      }

      // Plan generated — fire UX celebration and close
      toast.success(data.notification || (fr ? 'Plan adapté généré !' : 'Adapted plan generated!'), {
        duration: 7000,
      });
      onPlanGenerated && onPlanGenerated(data.plan);
      onOpenChange(false);
      // Slight delay before navigating, so toast is seen
      setTimeout(() => { navigate('/workout'); }, 600);
      reset();
    } catch (err) {
      ErrorLogger.log(err, 'Injury_Workout_Generate', {
        severity: 'CRITICAL',
        zone, severity_score: severity[0],
      });
      const detail = err?.response?.data?.detail;
      toast.error(detail || (fr ? "Erreur — réessaie." : 'Error — retry.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Block screen (medical shield OR paywall) ──────────────────────────
  const renderBlocked = () => {
    if (!result) return null;
    const isPaywall = result.block_reason === 'tier_paywall';
    const Icon = isPaywall ? Lock : ShieldAlert;
    const iconColor = isPaywall ? 'text-amber-500' : 'text-red-500';
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
        data-testid="injury-block-view"
      >
        <div className={`flex items-start gap-3 p-4 rounded-2xl border bg-card`}>
          <Icon className={`w-8 h-8 ${iconColor} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <h3 className="font-heading text-lg uppercase tracking-tight mb-1">{result.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{result.message}</p>
          </div>
        </div>

        {result.show_calendly && result.calendly_url && (
          <Button
            asChild
            size="lg"
            className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90"
            data-testid="injury-block-calendly-btn"
          >
            <a href={result.calendly_url} target="_blank" rel="noopener noreferrer">
              <CalendarDays className="w-4 h-4 mr-2" />
              {fr ? 'Réserver avec ton coach' : 'Book with your coach'}
            </a>
          </Button>
        )}

        {isPaywall && (
          <Button
            size="lg"
            className="w-full bg-gradient-to-r from-primary to-pink-500 hover:opacity-90"
            data-testid="injury-block-upgrade-btn"
            onClick={() => { onOpenChange(false); navigate('/upgrade'); }}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {fr ? 'Passer au Premium' : 'Upgrade to Premium'}
          </Button>
        )}

        <Button variant="outline" className="w-full" onClick={() => { reset(); onOpenChange(false); }}>
          {fr ? 'Fermer' : 'Close'}
        </Button>
      </motion.div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="injury-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading uppercase tracking-tight">
            <Activity className="w-5 h-5 text-primary" />
            {fr ? 'Mode Blessure' : 'Injury Mode'}
          </DialogTitle>
          <DialogDescription>
            {fr
              ? "Décris ta blessure — l'IA va adapter ton plan d'entraînement."
              : "Describe your injury — the AI will adapt your training plan."}
          </DialogDescription>
        </DialogHeader>

        {result?.blocked ? renderBlocked() : (
          <div className="space-y-5">
            {/* Zone */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                {fr ? 'Zone affectée' : 'Affected zone'} <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {ZONES.map((z) => (
                  <button
                    key={z.id}
                    type="button"
                    onClick={() => setZone(z.id)}
                    className={`text-sm py-2 px-3 rounded-xl border transition-all ${
                      zone === z.id
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/40'
                        : 'bg-secondary/30 border-border hover:border-primary/50'
                    }`}
                    data-testid={`injury-zone-${z.id}`}
                  >
                    {fr ? z.fr : z.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Severity slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">
                  {fr ? 'Douleur / sévérité' : 'Pain / severity'}
                </Label>
                <span
                  className={`text-2xl font-bold ${
                    severity[0] >= 8 ? 'text-red-500' :
                    severity[0] >= 5 ? 'text-amber-500' : 'text-emerald-500'
                  }`}
                  data-testid="injury-severity-value"
                >
                  {severity[0]}/10
                </span>
              </div>
              <Slider
                value={severity}
                onValueChange={setSeverity}
                min={1}
                max={10}
                step={1}
                data-testid="injury-severity-slider"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                {severity[0] >= 8
                  ? (fr ? '⚠️ À ce niveau, on va te rediriger en sécurité.' : '⚠️ At this level, we redirect for safety.')
                  : severity[0] >= 5
                    ? (fr ? 'Modérée — adaptation possible.' : 'Moderate — adaptation possible.')
                    : (fr ? 'Mineure — bon candidat à l\'adaptation IA.' : 'Minor — good fit for AI adaptation.')}
              </p>
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                {fr ? 'Décris ce que tu ressens' : 'Describe what you feel'}
              </Label>
              <Textarea
                placeholder={fr
                  ? "Ex: Tendinite légère après ma course, la rotation extérieure est sensible..."
                  : "E.g. Mild tendinitis after running, outward rotation is sensitive..."}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
                className="text-sm"
                data-testid="injury-description"
              />
            </div>

            {/* Objective */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                {fr ? 'Ton objectif' : 'Your goal'}
              </Label>
              <div className="flex gap-2">
                {OBJECTIVES.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setObjective(o.id)}
                    className={`flex-1 text-xs py-2 px-2 rounded-xl border transition-all ${
                      objective === o.id
                        ? 'bg-primary/20 border-primary text-foreground'
                        : 'bg-secondary/30 border-border hover:border-primary/50 text-muted-foreground'
                    }`}
                    data-testid={`injury-objective-${o.id}`}
                  >
                    {fr ? o.fr : o.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || !zone}
              className="w-full bg-gradient-to-r from-primary to-pink-500 hover:opacity-90 font-heading uppercase tracking-wide"
              data-testid="injury-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {fr ? 'Génération en cours...' : 'Generating...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {fr ? 'Adapter mon entraînement' : 'Adapt my training'}
                </>
              )}
            </Button>

            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
              {fr
                ? "🛡️ Bouclier médical actif — pour les blessures graves, on te redirige vers un professionnel."
                : "🛡️ Medical shield active — for serious injuries, we'll redirect you to a professional."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InjuryCaptureDialog;
